import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { createPrivateAPI, LimitedPrivateAPI, PlatformPrivateServices } from '@wix/editor-platform-private-api';
import { DMEventsBridge, PlatformAppEventEmitter, PlatformLifecycleEvent, PlatformPrivateEventEmitter } from '@wix/public-editor-platform-events';
import { PlatformWorkerManager, SingleWorkerStrategy } from './PlatformWorkerManager';
import { AppRunner } from './PlatformWorkerManager/AppRunner';
import { getWorkerUrl } from './PlatformWorkerManager/getWorkerUrl';
import { PlatformFrameStrategy } from './PlatformWorkerManager/PlatformFrameStrategy';
import { ApplicationContext, EnvironmentContext, PlatformEnvironment } from '@wix/editor-platform-contexts';
import { ManifestsManager } from '@wix/editor-platform-manifest-services';
// TODO: replace with ambassador-tokencreator-v1-token/types

import { createEditorPlatformWorkerError, EditorPlatformWorkerErrorCode } from './errors';
import { PlatformDevTools } from './PlatformDevTools';
function subscribeOnHostEvents(ps) {
  const dmEventsBridge = new DMEventsBridge(ps.events);
  const callback = event => {
    dmEventsBridge.notify(event);
  };
  ps.ds.platform.subscribeOnAppEvents(callback);
  ps.ds.platform.subscribeOnCustomAppEvents(callback);
}
const ACCESS_TOKEN_EXPIRY_GRACE_PERIOD = 30 * 60 * 1000; // half an hour

function hasEnoughTimeUntilExpiry(expires, expiryBuffer) {
  if (expiryBuffer === void 0) {
    expiryBuffer = ACCESS_TOKEN_EXPIRY_GRACE_PERIOD;
  }
  const now = Date.now();
  const expiryTime = expires ? new Date(expires).getTime() : null;
  return expiryTime && expiryTime - now > expiryBuffer;
}

/**
 * TODO: move this class somewhere other package
 * probably do it as soon as we move to the new repo with the DI
 */
export class PlatformInfra {
  constructor(hostType, apis) {
    this.hostType = hostType;
    this.apis = apis;
    _defineProperty(this, "ps", void 0);
    _defineProperty(this, "workerManager", void 0);
    _defineProperty(this, "privateAPI", void 0);
    _defineProperty(this, "limitedPrivateAPI", void 0);
    const platformEvents = new PlatformPrivateEventEmitter();
    const manifestsManager = new ManifestsManager(this.apis.documentServicesAPI, this.apis.hostAPI, platformEvents, this.apis.editorPointerAPI, {
      getSDKHostContext: this.getSDKContext.bind(this),
      getSDKAuth: this.getSDKAuth.bind(this)
    });
    this.ps = new PlatformPrivateServices(this.apis.documentServicesAPI, this.apis.hostAPI, platformEvents, this.apis.experimentsAPI, manifestsManager, this.apis.editorPointerAPI);
    this.privateAPI = createPrivateAPI(this.hostType, this.ps);
    this.limitedPrivateAPI = new LimitedPrivateAPI(this.privateAPI);
    const workerStrategy = new SingleWorkerStrategy(getWorkerUrl, this.limitedPrivateAPI, platformEvents);
    const frameStrategy = new PlatformFrameStrategy(this.ps, this.limitedPrivateAPI, platformEvents);
    this.workerManager = new PlatformWorkerManager(this.privateAPI, workerStrategy, frameStrategy, platformEvents);
    if (this.isDebugMode()) {
      const devtools = new PlatformDevTools(this.getPublicAPI(), this.ps.manifestsManager);
      devtools.register();
    }
  }
  isDebugMode() {
    return typeof window !== 'undefined' && typeof window.location !== 'undefined' && new URLSearchParams(window.location.search).has('debug');
  }
  getPublicAPI() {
    return {
      getSDKHost: this.getSDKHost.bind(this),
      getSDKAuth: this.getSDKAuth.bind(this),
      getSDKHostContext: this.getSDKContext.bind(this),
      getPlatformSDKHost: this.getSDKHost.bind(this),
      connectFrame: this.connectFrame.bind(this),
      getComponentsService: this.getComponentsService.bind(this)
    };
  }
  getComponentsService() {
    return this.ps.manifestsManager.getComponentsService();
  }
  getSDKAuth(props) {
    if (props === void 0) {
      props = {};
    }
    let instance;
    const appDefinitionId = props.appDefinitionId;
    return {
      getAuthHeaders: async () => {
        var _instance;
        if (!appDefinitionId) {
          throw createEditorPlatformWorkerError(EditorPlatformWorkerErrorCode.AuthEmptyAppDefinitionId);
        }
        if (!instance || hasEnoughTimeUntilExpiry(instance.expires)) {
          instance = await this.privateAPI.auth.getAccessToken(appDefinitionId, await this.privateAPI.info.getMetaSiteId());
        }
        if (!((_instance = instance) != null && _instance.token)) {
          console.error(createEditorPlatformWorkerError(EditorPlatformWorkerErrorCode.AuthEmptyAppAuthInstance).withAppDefinitionId(appDefinitionId));
          return {
            headers: {
              Authorization: ''
            }
          };
        }
        return {
          headers: {
            Authorization: instance.token
          }
        };
      }
    };
  }
  getSDKHost(props) {
    if (!props || !(props != null && props.editorPointer) && !props.contextBuilder) {
      throw createEditorPlatformWorkerError(EditorPlatformWorkerErrorCode.PlatformSDKHostInitError, 'editorPointer or contextBuilder is required to initialize platform SDK host');
    }
    const environmentContext = new EnvironmentContext({
      environment: PlatformEnvironment.ComponentPanel,
      privateApi: this.privateAPI,
      events: new PlatformAppEventEmitter()
    });
    let manifestContextId = null;
    try {
      const {
        contextBuilder,
        editorPointer
      } = props;
      manifestContextId = this.ps.manifestsManager.buildManifestContext(editorPointer ? builder => {
        builder.byEditorPointer(editorPointer, editorElement => {
          builder.withEditorElement(editorPointer, editorElement);
        });
      } : contextBuilder);
    } catch (e) {}
    return {
      channel: {
        observeState: async () => {
          return {
            disconnect() {}
          };
        }
      },
      environmentContext,
      applicationContext: new ApplicationContext({
        manifestContextId,
        appDefinitionId: props.appDefinitionId
      }, environmentContext)
    };
  }
  getSDKContext(props) {
    const host = this.getSDKHost(props);
    return {
      getHost: () => host,
      getAuth: () => this.getSDKAuth({
        appDefinitionId: props.appDefinitionId
      }),
      replaceHost: builder => {
        var _host$applicationCont;
        const manifestContextId = (_host$applicationCont = host.applicationContext) == null ? void 0 : _host$applicationCont.getManifestContextId();
        if (!manifestContextId) {
          return;
        }
        this.ps.manifestsManager.replaceManifestContext(manifestContextId, builder);
      }
    };
  }
  async connectFrame(appDefinitionId, iframe) {
    return this.workerManager.connectFrame(appDefinitionId, iframe);
  }
  startManifestServices() {
    this.ps.manifestsManager.start();
  }
  async startApps() {
    const runner = new AppRunner(this.ps, this.workerManager);
    await runner.start();
    subscribeOnHostEvents(this.ps);
    this.workerManager.events.notify({
      type: PlatformLifecycleEvent.EditorReady,
      payload: {},
      meta: {}
    });
  }
}
//# sourceMappingURL=PlatformInfra.js.map